/**
 * ForgotPassword — request a password reset email.
 *
 * Always shows the same success message regardless of whether the email
 * belongs to a real account (mirrors the backend's enumeration-safe
 * response). The user follows the link in their inbox to `/reset-password`.
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Loader2, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { api } from '../utils/api';
import Disclaimer from '../components/Disclaimer';
import RotatingScripture from '../components/RotatingScripture';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    setIsLoading(true);
    try {
      const res = await api.forgotPassword(email.trim());
      // The backend always returns 200 with a generic message. We surface
      // that message so the user knows what to do next.
      toast.success('Check your inbox', {
        description: res?.message || 'If an account exists, a reset link was just sent.',
      });
      setSubmitted(true);
    } catch (err) {
      // Even unexpected network errors shouldn't reveal enumeration info,
      // so we still show the generic "check your email" state.
      console.error('Forgot password request failed', err);
      toast.error('Could not submit request', {
        description: err?.message || 'Please try again in a moment.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--sd-bg)] flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 py-12 relative overflow-hidden">
        {/* Ambient background */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            background:
              'radial-gradient(circle at 20% 15%, rgba(212,175,55,0.10) 0%, transparent 45%), radial-gradient(circle at 85% 90%, rgba(183,110,121,0.12) 0%, transparent 55%)',
          }}
        />

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative w-full max-w-md"
        >
          <div className="mb-6">
            <Link
              to="/login"
              data-testid="forgot-password-back-link"
              className="inline-flex items-center gap-1.5 text-sm text-[var(--sd-muted-fg)] hover:text-[var(--sd-fg)] transition-colors"
            >
              <ArrowLeft size={14} />
              Back to sign in
            </Link>
          </div>

          <Card className="glass-card p-8" data-testid="forgot-password-card">
            <div className="text-center mb-6">
              <div
                className="mx-auto mb-4 w-14 h-14 rounded-full flex items-center justify-center"
                style={{
                  background: 'rgba(183, 110, 121, 0.10)',
                  border: '1px solid rgba(183, 110, 121, 0.25)',
                }}
              >
                <Mail size={22} className="text-[var(--sd-primary)]" />
              </div>
              <h1
                className="text-2xl font-semibold text-[var(--sd-fg)]"
                style={{ fontFamily: '"Playfair Display", serif' }}
              >
                Forgot your password?
              </h1>
              <p className="mt-2 text-sm text-[var(--sd-muted-fg)] leading-relaxed">
                No stress. Enter your account email and we&apos;ll send you a link
                to choose a new one. Your existing downloads stay right where they are.
              </p>
            </div>

            {submitted ? (
              <div
                className="space-y-4 text-center"
                data-testid="forgot-password-sent-state"
              >
                <p className="text-sm text-[var(--sd-fg-secondary)] leading-relaxed">
                  If an account exists for <strong>{email}</strong>, a reset
                  link has been sent. The link expires in 60 minutes.
                </p>
                <p className="text-xs text-[var(--sd-muted-fg)] italic">
                  Didn&apos;t receive it? Check your spam folder or try again.
                </p>
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setSubmitted(false)}
                    className="flex-1 border-[var(--sd-border)] text-[var(--sd-fg)]"
                    data-testid="forgot-password-resend-button"
                  >
                    Send again
                  </Button>
                  <Button
                    onClick={() => navigate('/login')}
                    className="flex-1 bg-[var(--sd-primary)] hover:brightness-110 text-[var(--sd-primary-fg)] font-semibold"
                    data-testid="forgot-password-back-to-login"
                  >
                    Back to sign in
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label
                    htmlFor="email"
                    className="block text-xs uppercase tracking-widest text-[var(--sd-muted-fg)] mb-2"
                  >
                    Email address
                  </label>
                  <Input
                    id="email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="h-11 bg-[var(--sd-surface)] border-[var(--sd-border)] text-[var(--sd-fg)] placeholder:text-[var(--sd-muted-fg)]"
                    data-testid="forgot-password-email-input"
                    disabled={isLoading}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-11 bg-[var(--sd-primary)] hover:brightness-110 text-[var(--sd-primary-fg)] font-semibold rounded-xl"
                  data-testid="forgot-password-submit-button"
                >
                  {isLoading ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Sending link…
                    </>
                  ) : (
                    'Send reset link'
                  )}
                </Button>

                <p className="text-[11px] text-center text-[var(--sd-muted-fg)] leading-relaxed pt-2">
                  Remembered it after all?{' '}
                  <Link
                    to="/login"
                    className="underline underline-offset-2 text-[var(--sd-primary)]"
                  >
                    Back to sign in
                  </Link>
                </p>
              </form>
            )}
          </Card>

          <div className="mt-8">
            <RotatingScripture variant="compact" />
          </div>
        </motion.div>
      </div>

      <Disclaimer />
    </div>
  );
};

export default ForgotPassword;
